import { WorksheetAssociatedTestSection } from 'Components/Worksheets/Core';
import { toMessageProps } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import ReImportButton from 'Components/Worksheets/Core/ReImportButton';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { GrainSize } from 'Components/Worksheets/MoistureContentWorksheet/types';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { Message } from 'Data/ViewModels/IApiDataModel';
import { valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { Guid } from 'Lib/Guid';
import { usePreviousAfterFirstLoad } from 'Lib/Hooks';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, DateTimeInput, Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, SectionGroup } from '../../../../Layout';
import externalTestMethods from '../../../ExternalTestMethods';

let AsPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, prefixName }) => {
  const dispatch = useDispatch();
  const stabiliserUsed = useSelector(
    pipe(createWorksheetSelector!('Preparation.StabiliserUsed'), valueOrDefault(false))
  );

  const stabiliserAddedAtSampling = useSelector(
    pipe(createWorksheetSelector!('Preparation.StabiliserAddedAtSampling'), valueOrDefault(false))
  );

  const isTestPortionMoistureContentDetermined = useSelector(
    pipe(createWorksheetSelector!('Preparation.FineMoistureContentDetermined'), valueOrDefault(false))
  );

  const importedFineMoistureTestId = useSelector(
    createWorksheetSelector!('Preparation.AssociatedFineMoisture.ImportedTest.Id')
  ) as Guid;

  const materialNotSuitableDueToOversize = useSelector(
    pipe(createWorksheetSelector!('Preparation.ScreeningASTM.MaterialNotSuitableDueToOversize'), valueOrDefault(false))
  );

  const isTestPortionMoistureContentExternal = useSelector(
    pipe(createWorksheetSelector!('Preparation.AssociatedFineMoisture.AssociatedTestType'), (t) => t === 'external')
  );

  const previousIsTestPortionMoistureContentDetermined = usePreviousAfterFirstLoad(
    isTestPortionMoistureContentDetermined
  );

  const grainSize = useSelector(pipe(createWorksheetSelector!('Preparation.FineGrainSize'))) as GrainSize;

  const grainSizeOptions: Array<{ label: string; value: GrainSize }> = [
    { label: 'Fine', value: GrainSize.Fine },
    { label: 'Medium', value: GrainSize.Medium },
    { label: 'Coarse', value: GrainSize.Coarse },
  ];

  /**
   * Clear entered test portion moisture content when we mark it as not being determined
   *
   * TODO: May be better as backend behaviour but we have to revise how existing MDD worksheets behave.
   */
  useEffect(() => {
    if (previousIsTestPortionMoistureContentDetermined && !isTestPortionMoistureContentDetermined)
      dispatch(
        actions.setValue({
          name: prefixName!('Preparation.AssociatedFineMoisture'),
          value: {},
          sync: true,
        })
      );
  }, [dispatch, prefixName, previousIsTestPortionMoistureContentDetermined, isTestPortionMoistureContentDetermined]);

  /**
   * Messages for oversize determination results
   *
   * These are not shown on the worksheet so we add an explicit section for these messages
   * (which contain important information about how the test is to be performed based on these results)
   */
  const oversizeDeterminationMessages = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.ScreeningASTM.Messages'),
      (m) => (m as Array<Message>)?.filter((m) => ['OversizeSieve', 'OversizePercentWet'].includes(m.PropertyName)),
      (m) => m.map(toMessageProps)
    ),
    deepEquals
  );

  return (
    <>
      <ListFieldSection>
        <Field input={<Checkbox />} name="Preparation.StabiliserUsed" />
        <Field
          visible={stabiliserUsed}
          input={<RadioGroup />}
          name={`Preparation.StabiliserAddedAtSampling`}
          options={[
            { label: 'At Sampling', value: true },
            { label: 'Post Sampling', value: false },
          ]}
        />
        <FieldSet>
          <Field
            visible={stabiliserUsed}
            input={stabiliserAddedAtSampling ? <TextOutput /> : <DateTimeInput />}
            name="Preparation.StabiliserAddedAtDateTimeLocal"
            dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
          />
          <ReImportButton name="Preparation.ImportedStabiliserSource" />
        </FieldSet>
      </ListFieldSection>

      <SectionGroup>
        <WorksheetAssociatedTestSection
          name="Preparation.AssociatedOversizeDetermination"
          testType="Oversize Determination"
          associatedTestModel={{
            measured: true,
          }}
        />
        <ListFieldSection messages={oversizeDeterminationMessages} />
      </SectionGroup>

      {!materialNotSuitableDueToOversize && (
        <SectionGroup>
          <ListFieldSection>
            <Field
              input={<Checkbox />}
              name="Preparation.FineMoistureContentDetermined"
              confirm={isTestPortionMoistureContentDetermined}
              confirmText={
                'This action will cause data entered for the test portion moisture content to be lost. Are you sure you want to complete this action?'
              }
            />
          </ListFieldSection>

          <WorksheetAssociatedTestSection
            label="Test Portion Moisture Content"
            visible={isTestPortionMoistureContentDetermined}
            name="Preparation.AssociatedFineMoisture"
            testType="Moisture Content"
            associatedTestModel={{
              autoImported: true,
              imported: {
                importedTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<TextOutput />}
                      name="Preparation.FineGrainSize"
                      value={grainSizeOptions.find((o) => o.value === grainSize)?.label}
                      label="Grain Size"
                    />
                    <Field input={<TextOutput />} name="Preparation.FineMoistureContent" label="Moisture Content" />
                  </ListFieldSection>
                ),
                importModel: {
                  Id: importedFineMoistureTestId,
                },
              },
              external: {
                testMethods: [
                  externalTestMethods.moistureContent.AS_1289_2_1_1,
                  externalTestMethods.moistureContent.AS_1289_2_1_2,
                  externalTestMethods.moistureContent.AS_1289_2_1_4,
                  externalTestMethods.moistureContent.AS_1289_2_1_5,
                  externalTestMethods.moistureContent.AS_1289_2_1_6,
                ],
                permitOtherTestMethods: false,
                externalTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<NumericInput />}
                      name="Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent"
                    />
                  </ListFieldSection>
                ),
              },
            }}
          />
          {(!isTestPortionMoistureContentDetermined || isTestPortionMoistureContentExternal) && (
            <ListFieldSection>
              <Field input={<RadioGroup />} name="Preparation.FineGrainSize" options={grainSizeOptions} />
            </ListFieldSection>
          )}
        </SectionGroup>
      )}
    </>
  );
};

AsPreparationSection = withInjectedProps(AsPreparationSection);

export { AsPreparationSection };
export default AsPreparationSection;
